// AF-3.19 P1 — Creator chain: Decisions. Real route (AF-3.6), reuses the
// existing DecisionCard design-system component. Field names verified
// against creative_decision.py's document construction this session;
// mapped explicitly below where the real field name differs from
// DecisionCard's prop name (readiness_state -> readiness,
// constraint_results.hard_constraint_violations -> hard_constraints_violated)
// rather than modifying the shared component.

import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { BrandContextBar } from '@/components/brand/BrandContextBar';
import { DecisionCard } from '@/components/brand/DecisionCard';
import { EmptyState, ErrorState, LoadingState } from '@/components/brand/StateViews';
import { EvidenceDrawer } from '@/components/brand/EvidenceDrawer';
import { Button } from '@/components/ui/button';
import { useBrandContext } from '@/hooks/useBrandContext';
import { creativeDecisionAPI } from '@/lib/intelligenceApi';
import { CaretRight } from '@phosphor-icons/react';

// AF-3.19 Phase 7 — minimal Strategy -> Ideas -> Review -> Decisions ->
// Production -> Distribution stepper, shared shape with the other Creator
// chain pages, so this page's position in the sequence is obvious at a
// glance.
const CHAIN_STAGES = ['Strategy', 'Ideas', 'Review', 'Decisions', 'Production', 'Distribution'];

function ChainProgress({ current }) {
  return (
    <div data-testid="creator-chain-progress" className="flex items-center gap-1.5 flex-wrap mb-3">
      {CHAIN_STAGES.map((stage, i) => (
        <span key={stage} className="flex items-center gap-1.5">
          <span className={`text-[10px] font-mono uppercase ${stage === current ? 'text-[var(--accent)] font-medium' : 'text-[var(--text-tertiary)]'}`}>{stage}</span>
          {i < CHAIN_STAGES.length - 1 && <CaretRight size={10} className="text-[var(--text-tertiary)]" />}
        </span>
      ))}
    </div>
  );
}

export default function CreatorDecisionsPage() {
  const ctx = useBrandContext();
  const [decisions, setDecisions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [evidenceFor, setEvidenceFor] = useState(null);

  const load = useCallback(() => {
    if (!ctx.ready) return;
    setLoading(true); setError(null);
    creativeDecisionAPI.list(ctx.workspaceId, { entity_type: ctx.entityType, entity_id: ctx.entityId })
      .then((r) => setDecisions(r.data?.decisions || r.data || []))
      .catch((e) => setError(e))
      .finally(() => setLoading(false));
  }, [ctx.ready, ctx.workspaceId, ctx.entityType, ctx.entityId]);

  useEffect(() => { load(); }, [load]);

  return (
    <DashboardLayout>
      <div data-testid="creator-decisions-page" className="max-w-5xl mx-auto">
        <div className="mb-4">
          <h1 className="font-display text-[20px] font-bold text-[var(--text-primary)] mb-1">Creative Decisions</h1>
          <p className="text-[12px] text-[var(--text-secondary)]">SELECT / REVISE / REJECT verdicts. A decision here is not an approval to distribute — see Distribution for that boundary.</p>
        </div>

        <ChainProgress current="Decisions" />

        <BrandContextBar ctx={ctx} />

        {!ctx.ready && !ctx.loadingClients && <EmptyState title="Select a workspace and entity" description="Choose a brand workspace and entity above to view its decisions." />}
        {loading && <LoadingState />}
        {error && <ErrorState error={error} onRetry={load} />}

        {!loading && !error && ctx.ready && decisions.length === 0 && (
          <EmptyState title="No decision made yet" description="A decision is produced once candidates have been reviewed for this entity." />
        )}

        {!loading && !error && decisions.length > 0 && (
          <div className="space-y-3" data-testid="creator-decisions-list">
            {decisions.map((d) => (
              <div key={d.id}>
                <DecisionCard
                  testId="creator-decision-card"
                  decision={{
                    ...d,
                    readiness: d.readiness_state,
                    hard_constraints_violated: d.constraint_results?.hard_constraint_violations || [],
                    revision_requirements: d.revision_reasons || [],
                  }}
                />
                <Button data-testid="creator-decision-evidence-trigger" variant="link" size="sm" className="text-[11px] text-[var(--accent)] px-0" onClick={() => setEvidenceFor(d)}>
                  Why this decision? →
                </Button>
              </div>
            ))}
          </div>
        )}

        {evidenceFor && (
          <EvidenceDrawer
            open={!!evidenceFor}
            onOpenChange={(open) => !open && setEvidenceFor(null)}
            title="Why Martian made this decision"
            evidence={[]}
            provenance={{
              creative_review_id: evidenceFor.creative_review_id ? [evidenceFor.creative_review_id] : [],
              creative_strategy_id: evidenceFor.creative_strategy_id ? [evidenceFor.creative_strategy_id] : [],
              creative_idea_id: evidenceFor.creative_idea_id ? [evidenceFor.creative_idea_id] : [],
              creator_artifact_id: evidenceFor.creator_artifact_id ? [evidenceFor.creator_artifact_id] : [],
              media_asset_id: evidenceFor.media_asset_id ? [evidenceFor.media_asset_id] : [],
            }}
            causality={evidenceFor.decision_reason}
          />
        )}
      </div>
    </DashboardLayout>
  );
}
